import { Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Meta } from "@/@types/paginated";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStoreEmailFilterActions } from "../filter/hooks/actions";
import { useFilterState } from "../filter/hooks/filterState";
interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  meta: Meta;
}

export function DataTablePagination<TData>({
  table,
  meta,
}: DataTablePaginationProps<TData>) {
  const filterState = useFilterState();
  const { updateFilter } = useStoreEmailFilterActions();
  const { filters } = filterState || { filters: {} };
  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {table.getFilteredRowModel().rows.length} linha(s) encontrada(s).
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Linhas por página</p>
          <Select
            value={`${meta.perPage}`}
            onValueChange={(value) => {
              updateFilter({
                key: "meta",
                value: { 
                  ...filters.meta,
                  perPage: parseInt(value, 10),
                  page: 1 // Reset para primeira página ao mudar perPage
                },
              });
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={meta.perPage} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 25, 50, 100].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Página {meta.currentPage} de {meta.lastPage}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: { 
                  ...filters.meta,
                  page: 1 
                },
              });
            }}
            disabled={meta.prev === null}
          >
            <span className="sr-only">Ir para a primeira página</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: { 
                  ...filters.meta,
                  page: (filters.meta?.page || 1) - 1
                },
              });
            }}
            disabled={meta.prev === null}
          >
            <span className="sr-only">Ir para a página anterior</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: { 
                  ...filters.meta,
                  page: (filters.meta?.page || 1) + 1
                },
              });
            }}
            disabled={meta.next === null}
          >
            <span className="sr-only">Ir para a próxima página</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: { 
                  ...filters.meta,
                  page: meta.lastPage 
                },
              });
            }}
            disabled={meta.next === null}
          >
            <span className="sr-only">Ir para a última página</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
